import { z } from 'zod'
import { auth } from './auth'
import { http } from './http'
import { UserState } from '../types'
import { parseWithZod } from '../utils/parse-with-zod'
import { EmbeddedUserSchema } from '../schemas/schemas'

const QuickSightEmbedResponseSchema = z.object({
  embed_url: z.string(),
})

const DealStatsSchema = z.object({
  deal_id: z.number(),
  matter_number: z.string(),
  client_number: z.string(),
  date_created: z.string(),
  closing_date: z.string().nullable(),
  deletion_date: z.string().nullable(),
  responsible_lawyer_email: z.string().nullable(),
  last_accessed_date: z.string().nullable(),
  last_closing_book_created_at: z.string().nullable(),
  active_users: z.number().nullable(),
  days_active: z.number().nullable(),
  title: z.string(),
  deal_status_id: z.number().nullable(),
  deal_status_updated_at: z.string().nullable(),
  has_external_users: z.boolean().optional(),
  is_template: z.boolean().optional().default(false),
  is_org_wide: z.boolean().optional().default(false),
  created_by_email: z.string().nullable().optional(),
  owner_organization: z.string().nullable().optional(),
  created_by_name: z.string().nullable().optional(),
  created_by: EmbeddedUserSchema.pick({
    id: true,
    first_name: true,
    last_name: true,
    email: true,
  }).nullish(),
})

export type DealStat = z.infer<typeof DealStatsSchema>

export const UserStatsSchema = z.object({
  id: z.number().nullable(),
  auth_user_id: z.number(),
  first_name: z.string(),
  last_name: z.string(),
  active_deals: z.number(),
  days_active: z.number(),
  email: z.string(),
  state: z.nativeEnum(UserState),
  is_admin: z.boolean(),
  is_external: z.boolean(),
  is_invited_email: z.boolean().optional(),
  last_accessed: z.string().nullable(),
  organization_name: z.string().nullable().optional(),
  date_joined: z.string().nullable().optional(),
  deletion_date: z.string().nullable().optional(),
})

const UserStatsResponseSchema = z.array(UserStatsSchema)
const DealStatsResponseSchema = z.array(DealStatsSchema)

export const OrgAdminActivitySchema = z.object({
  id: z.number(),
  created_at: z.string(),
  initiator: z.object({
    id: z.number(),
    name: z.string(),
    email: z.string(),
  }),
  action: z.string(),
  target_user: z.object({
    id: z.number(),
    name: z.string(),
    email: z.string(),
  }),
  data: z
    .object({
      scheduled_date: z.string().optional(),
    })
    .nullable(),
})

type StatsParams = {
  organization: number
  with_activity: boolean
  start?: string
  end?: string
}

export const admin = {
  getAllDealStats: async (
    orgId: number,
    startDate: string | undefined,
    endDate: string | undefined,
    withActivity: boolean = false,
  ) => {
    const regionHost = auth.getUserRegionHost()

    const params: StatsParams = {
      organization: orgId,
      with_activity: withActivity,
    }

    if (withActivity) {
      params.start = startDate
      params.end = endDate
    }

    const result = await http.get(`${regionHost}/api/stats/deals`, params)
    return parseWithZod(DealStatsResponseSchema, result)
  },

  getUserStats: async (
    organizationId: number,
    startDate: string | undefined,
    endDate: string | undefined,
    withActivity: boolean = false,
  ) => {
    const regionHost = auth.getUserRegionHost()

    const params: StatsParams = {
      organization: organizationId,
      with_activity: withActivity,
    }

    if (withActivity) {
      params.start = startDate
      params.end = endDate
    }

    const result = await http.get(`${regionHost}/api/stats/users`, params)
    return parseWithZod(UserStatsResponseSchema, result)
  },

  getActiveDealStatuses: async (orgId: number) => {
    const regionHost = auth.getUserRegionHost()
    const url = `${regionHost}/api/organizations/${orgId}/active_deal_statuses`
    const result = await http.get(url)
    return parseWithZod(z.array(z.number()), result)
  },

  getQuickSightEmbedUrl: async (orgId: number) => {
    const regionHost = auth.getUserRegionHost()
    const url = `${regionHost}/api/organizations/${orgId}/generate_quicksight_embed_url`
    const result = await http.post(url, {})
    return parseWithZod(QuickSightEmbedResponseSchema, result)
  },

  getOrgAdminActivity: async () => {
    const result = await http.get(`/api/v1/org_admin_audit`)
    return parseWithZod(z.array(OrgAdminActivitySchema), result)
  },
}
